import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    User, Calendar, ClipboardList, Pill, Save, Loader2, ExternalLink,
    Shield, Download, Eye, FileText, HeartHandshake,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import SharedFormattedMarkdown from "@/components/FormattedMarkdown";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CONSULTATION_TYPES, STATUS_LABELS } from "@/lib/aga";

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));

export default function PatientPortal() {
    const { user } = useAuth();
    const [tab, setTab] = useState("plan");
    const [state, setState] = useState({ linked: null, patient: null });
    const [consultations, setConsultations] = useState([]);
    const [carePlan, setCarePlan] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [intakeLinks, setIntakeLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [me, cs, cp, rm, il] = await Promise.all([
                api.get("/portal/me"),
                api.get("/portal/my-consultations"),
                api.get("/portal/my-care-plan"),
                api.get("/reminders"),
                api.get("/portal/my-intake-links"),
            ]);
            setState(me.data);
            setConsultations(cs.data);
            setCarePlan(cp.data);
            setReminders(rm.data);
            setIntakeLinks(il.data);
        } catch (e) { console.error("PatientPortal: failed to load data", e); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    if (loading) return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></AppShell>;

    if (state.linked === false) {
        return (
            <AppShell>
                <div className="card-earthy max-w-xl">
                    <HeartHandshake size={32} className="text-[var(--brand-secondary)] mb-3" />
                    <h1 className="font-display text-2xl font-medium text-brand mb-2">Sua conta ainda não foi vinculada a um prontuário</h1>
                    <p className="text-[var(--text-secondary)]">
                        Peça ao Dr. Marcos ou à equipe da clínica para vincular seu e-mail <strong>{user?.email}</strong> ao seu prontuário. Após a vinculação, você poderá acessar seu plano de cuidado, consultas e lembretes.
                    </p>
                </div>
            </AppShell>
        );
    }

    const p = state.patient;

    return (
        <AppShell>
            <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Portal do paciente</div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Olá, {p?.full_name?.split(" ")[0] || user?.name?.split(" ")[0]}</h1>
                <p className="text-[var(--text-secondary)] mt-1">Seu espaço seguro para acompanhar seu cuidado.</p>
            </div>

            {intakeLinks.length > 0 && (
                <div className="card-earthy mb-6 bg-alt border-l-4 border-[var(--brand-secondary)]">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-brand font-medium mb-1"><ExternalLink size={16} /> Pré-consulta pendente</div>
                            <p className="text-sm text-[var(--text-secondary)]">
                                Você tem {intakeLinks.length} formulário{intakeLinks.length > 1 ? "s" : ""} de pré-consulta para preencher antes do atendimento.
                            </p>
                        </div>
                        <div className="flex flex-col gap-2">
                            {intakeLinks.slice(0, 2).map((l) => (
                                <a key={l.id} href={`/intake/${l.intake_token}`} target="_blank" rel="noreferrer" className="btn-primary text-sm">
                                    Abrir {typeLabel[l.consultation_type]?.split("—")[0]}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="border-b border-[rgba(30,63,51,0.1)] mb-6 flex flex-wrap gap-1">
                {[
                    { id: "plan", label: "Plano de cuidado", Icon: FileText },
                    { id: "consultations", label: "Minhas consultas", Icon: Calendar },
                    { id: "reminders", label: "Lembretes", Icon: Pill },
                    { id: "profile", label: "Meus dados", Icon: User },
                    { id: "privacy", label: "Privacidade (LGPD)", Icon: Shield },
                ].map((t) => {
                    const active = tab === t.id;
                    const N = t.Icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`portal-tab-${t.id}`}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 -mb-px ${active ? "border-brand text-brand" : "border-transparent text-muted-clinical hover:text-brand"}`}
                        >
                            <N size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "plan" && <CarePlanView plan={carePlan} />}
            {tab === "consultations" && <ConsultationsList list={consultations} />}
            {tab === "reminders" && <RemindersList list={reminders} />}
            {tab === "profile" && <ProfileForm patient={p} onSaved={load} />}
            {tab === "privacy" && <PrivacyPanel />}
        </AppShell>
    );
}

function CarePlanView({ plan }) {
    if (!plan || !plan.patient_plan) {
        return (
            <div className="card-earthy max-w-3xl">
                <FileText size={28} className="text-[var(--brand-secondary)] mb-3" />
                <h2 className="font-display text-xl font-medium text-brand mb-2">Nenhum plano de cuidado disponível ainda</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                    Assim que o Dr. Marcos finalizar seu plano de cuidado, ele aparecerá aqui.
                </p>
            </div>
        );
    }
    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card-earthy">
                <h2 className="font-display text-xl font-medium text-brand mb-3">Seu plano de cuidado</h2>
                <div className="prose-clinical text-sm">
                    <FormattedMarkdown text={plan.patient_plan} />
                </div>
            </div>
            <div className="space-y-4">
                {plan.medications?.length > 0 && (
                    <div className="card-earthy">
                        <h3 className="font-display text-sm font-medium text-brand-secondary uppercase tracking-wider mb-3">Medicamentos</h3>
                        <ul className="text-sm space-y-2">
                            {plan.medications.map((m, i) => (
                                <li key={i} className="p-2 rounded-lg bg-alt">
                                    <div className="font-medium text-brand">{m.name}</div>
                                    <div className="text-xs text-muted-clinical">{m.dose} · {m.freq}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {plan.alerts?.length > 0 && (
                    <div className="card-earthy border-l-4 border-[var(--brand-accent)]">
                        <h3 className="font-display text-sm font-medium text-[var(--brand-accent)] uppercase tracking-wider mb-2">Sinais de alerta</h3>
                        <ul className="text-sm space-y-1">
                            {plan.alerts.map((a, i) => <li key={i} className="flex gap-2"><span className="text-[var(--brand-accent)]">•</span>{a}</li>)}
                        </ul>
                    </div>
                )}
                {plan.follow_up_date && (
                    <div className="card-earthy">
                        <h3 className="font-display text-sm font-medium text-brand-secondary uppercase tracking-wider mb-2">Próximo retorno</h3>
                        <div className="text-sm font-medium text-brand">{plan.follow_up_date}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ConsultationsList({ list }) {
    if (list.length === 0) return <div className="card-earthy text-sm text-muted-clinical">Nenhuma consulta registrada.</div>;
    return (
        <div className="space-y-2 max-w-3xl">
            {list.map((c) => (
                <div key={c.id} data-testid={`portal-consult-${c.id}`} className="card-earthy">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-medium text-brand">{typeLabel[c.consultation_type]}</div>
                            <div className="text-xs text-muted-clinical mt-1">{c.scheduled_at || "Sem data agendada"} · {c.payment_model}</div>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-alt text-brand-secondary">{STATUS_LABELS[c.status]}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function RemindersList({ list }) {
    if (list.length === 0) return <div className="card-earthy text-sm text-muted-clinical">Nenhum lembrete cadastrado pelo médico.</div>;
    return (
        <div className="space-y-2 max-w-3xl">
            {list.map((r) => (
                <div key={r.id} className="card-earthy">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-alt flex items-center justify-center text-brand"><Pill size={16} /></div>
                        <div className="flex-1">
                            <div className="font-medium text-brand">{r.medication_name} <span className="text-sm text-muted-clinical font-normal">{r.dose}</span></div>
                            <div className="text-xs text-brand-secondary mt-1">{r.times_of_day.join(" · ")}</div>
                            {r.notes && <div className="text-xs text-[var(--text-secondary)] mt-1">{r.notes}</div>}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ProfileForm({ patient, onSaved }) {
    const [f, setF] = useState({
        phone: patient?.phone || "",
        address: patient?.address || "",
        city_uf: patient?.city_uf || "",
        zip_code: patient?.zip_code || "",
        caregiver_name: patient?.caregiver_name || "",
        caregiver_relationship: patient?.caregiver_relationship || "",
        caregiver_phone: patient?.caregiver_phone || "",
        emergency_contact_name: patient?.emergency_contact_name || "",
        emergency_contact_phone: patient?.emergency_contact_phone || "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/portal/me", f);
            toast.success("Dados atualizados");
            onSaved && onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="card-earthy max-w-3xl space-y-4">
            <div>
                <h2 className="font-display text-xl font-medium text-brand">Meus dados</h2>
                <p className="text-sm text-muted-clinical">Você pode atualizar seus contatos e informações do cuidador. Dados clínicos (diagnósticos, medicamentos) são gerenciados pelo médico.</p>
            </div>
            <div className="text-sm bg-alt p-3 rounded-lg space-y-1">
                <div><strong>Nome:</strong> {patient?.full_name}</div>
                <div><strong>Nascimento:</strong> {patient?.birth_date || "—"} · <strong>Sexo:</strong> {patient?.sex || "—"}</div>
                <div><strong>Convênio:</strong> {patient?.insurance || "—"}</div>
                <div className="text-xs text-muted-clinical mt-1">Para alterar nome ou data de nascimento, contate a clínica.</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Telefone" v={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="portal-phone" />
                <Field label="CEP" v={f.zip_code} onChange={(v) => setF({ ...f, zip_code: v })} />
                <Field label="Endereço" v={f.address} onChange={(v) => setF({ ...f, address: v })} colSpan="md:col-span-2" />
                <Field label="Cidade / UF" v={f.city_uf} onChange={(v) => setF({ ...f, city_uf: v })} />
            </div>
            <div className="divider-organic" />
            <h3 className="font-display text-sm font-medium text-brand uppercase tracking-wider">Cuidador principal</h3>
            <div className="grid md:grid-cols-3 gap-3">
                <Field label="Nome" v={f.caregiver_name} onChange={(v) => setF({ ...f, caregiver_name: v })} />
                <Field label="Parentesco" v={f.caregiver_relationship} onChange={(v) => setF({ ...f, caregiver_relationship: v })} />
                <Field label="Telefone" v={f.caregiver_phone} onChange={(v) => setF({ ...f, caregiver_phone: v })} />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Contato de emergência" v={f.emergency_contact_name} onChange={(v) => setF({ ...f, emergency_contact_name: v })} />
                <Field label="Telefone de emergência" v={f.emergency_contact_phone} onChange={(v) => setF({ ...f, emergency_contact_phone: v })} />
            </div>
            <button data-testid="portal-save-btn" disabled={saving} className="btn-primary">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar
            </button>
        </form>
    );
}

function Field({ label, v, onChange, colSpan, testId }) {
    return (
        <div className={colSpan || ""}>
            <label className="label-clinical">{label}</label>
            <input data-testid={testId} value={v || ""} onChange={(e) => onChange(e.target.value)} className="input-clinical" />
        </div>
    );
}

function PrivacyPanel() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/portal/access-logs").then(({ data }) => { setLogs(data); setLoading(false); });
    }, []);

    const exportData = async () => {
        const { data } = await api.get("/portal/my-data-export");
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `meus_dados_${Date.now()}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Seus dados foram baixados");
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
            <div className="card-earthy">
                <div className="flex items-center gap-2 mb-3">
                    <Shield size={18} className="text-brand" />
                    <h2 className="font-display text-lg font-medium text-brand">Seus direitos (LGPD)</h2>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                    A Lei Geral de Proteção de Dados (LGPD) garante a você os direitos abaixo. Nossa plataforma mantém registro de todos os acessos aos seus dados.
                </p>
                <ul className="text-sm space-y-2 mb-4">
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Acessar todos os seus dados</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Corrigir dados incorretos ou incompletos</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Baixar seus dados em formato portável (JSON)</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Saber quem acessou seus dados e quando</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Revogar o consentimento a qualquer momento</li>
                </ul>
                <button data-testid="portal-export-btn" onClick={exportData} className="btn-primary text-sm"><Download size={14} /> Baixar meus dados</button>
            </div>

            <div className="card-earthy">
                <div className="flex items-center gap-2 mb-3">
                    <Eye size={18} className="text-brand" />
                    <h2 className="font-display text-lg font-medium text-brand">Registro de acessos</h2>
                </div>
                {loading ? <p className="text-sm text-muted-clinical">Carregando…</p> : (
                    <div className="max-h-[400px] overflow-y-auto space-y-1.5 text-xs">
                        {logs.slice(0, 30).map((l) => (
                            <div key={l.id} className="p-2 rounded-lg bg-alt flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-brand">{l.action} · {l.target}</div>
                                    <div className="text-muted-clinical">{l.role}</div>
                                </div>
                                <div className="text-muted-clinical">{new Date(l.created_at).toLocaleString("pt-BR")}</div>
                            </div>
                        ))}
                        {logs.length === 0 && <p className="text-muted-clinical">Nenhum acesso registrado ainda.</p>}
                    </div>
                )}
            </div>
        </div>
    );
}

function FormattedMarkdown({ text }) {
    return <SharedFormattedMarkdown text={text} />;
}
